import Button from '@shared/components/Button/Button';
import { ClientEvent, MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ReactComponent as Notification } from 'assets/icons/Notification.svg';
import { ReactComponent as X } from 'assets/icons/X.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import useSoundSettings from 'hooks/useSoundSettings/useSoundSettings';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { APP_ROUTES } from 'routes/routePaths';
import { FeatureFlag } from 'utils/Constants';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import testingConstants from 'utils/testingConstants';
import { isGuestModeEnabled } from 'utils/utils';
export const ONBOARDING_SEEN_KEY = 'notificationOnboardingSeen';
export const ONBOARDING_SEEN_LOCAL_STORAGE_KEY = 'meedio:notificationOnboardingSeen';

interface UserSettingsContent {
  notificationOnboardingSeen?: boolean;
  [key: string]: unknown;
}

const readAccountDataSeen = (matrixClient: MatrixClient): boolean => {
  try {
    const event = matrixClient.getAccountData(CustomMeedioMatrixEvents.USER_SETTINGS as never);
    const content = (event?.getContent<UserSettingsContent>() ?? {}) as UserSettingsContent;
    return content.notificationOnboardingSeen === true;
  } catch (error) {
    logger.warn('NotificationOnboardingCard: failed to read account data', error);
    return false;
  }
};

const readLocalStorageSeen = (): boolean => {
  try {
    return window.localStorage.getItem(ONBOARDING_SEEN_LOCAL_STORAGE_KEY) === 'true';
  } catch (error) {
    logger.warn('NotificationOnboardingCard: failed to read localStorage', error);
    return false;
  }
};

const writeLocalStorageSeen = (): void => {
  try {
    window.localStorage.setItem(ONBOARDING_SEEN_LOCAL_STORAGE_KEY, 'true');
  } catch (error) {
    logger.warn('NotificationOnboardingCard: failed to write localStorage', error);
  }
};

const hasSeen = (matrixClient: MatrixClient): boolean => readLocalStorageSeen() || readAccountDataSeen(matrixClient);

const NotificationOnboardingCard = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { matrixClient } = useMatrixContext();
  const { isMuted, setMuted } = useSoundSettings();
  const isMessengerEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const [isDismissed, setIsDismissed] = useState<boolean>(() => hasSeen(matrixClient));
  const [isWorking, setIsWorking] = useState(false);

  const isOnAuthRoute =
    location.pathname === APP_ROUTES.authRoot || location.pathname.startsWith(`${APP_ROUTES.authRoot}/`);
  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient.getUserId() ?? '');
  const isMessengerAudience = isMessengerEnabled && !isGuestUser;

  useEffect(() => {
    const handler = (event: { getType: () => string }) => {
      if (event.getType() !== CustomMeedioMatrixEvents.USER_SETTINGS) return;
      setIsDismissed(hasSeen(matrixClient));
    };
    matrixClient.on(ClientEvent.AccountData, handler);
    return () => {
      matrixClient.off(ClientEvent.AccountData, handler);
    };
  }, [matrixClient]);

  const markSeen = useCallback(async () => {
    writeLocalStorageSeen();
    setIsDismissed(true);

    const existingEvent = matrixClient.getAccountData(CustomMeedioMatrixEvents.USER_SETTINGS as never);
    const existing = (existingEvent?.getContent<UserSettingsContent>() ?? {}) as UserSettingsContent;
    const merged: UserSettingsContent = { ...existing, notificationOnboardingSeen: true };
    try {
      await matrixClient.setAccountData(CustomMeedioMatrixEvents.USER_SETTINGS as never, merged as never);
    } catch (error) {
      logger.warn('NotificationOnboardingCard: failed to persist dismissal to account data', error);
    }
  }, [matrixClient]);

  const handleEnableSound = useCallback(async () => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      if (isMuted) {
        try {
          await setMuted(false);
        } catch (error) {
          logger.warn('NotificationOnboardingCard: failed to enable sound', error);
        }
      }
      await markSeen();
    } finally {
      setIsWorking(false);
    }
  }, [isWorking, isMuted, setMuted, markSeen]);

  const handleDismiss = useCallback(async () => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await markSeen();
    } finally {
      setIsWorking(false);
    }
  }, [isWorking, markSeen]);

  if (isOnAuthRoute) return null;
  if (!isMessengerAudience) return null;
  if (isDismissed) return null;

  return (
    <div
      data-testid={testingConstants.notificationOnboardingCard}
      className="bg-white border-gray-30 fixed bottom-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-2xl border p-4 shadow-lg"
      role="dialog"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="bg-primary-50 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
          <Notification className="stroke-white h-4 w-4 stroke-2" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-size-md text-black font-semibold">{t('messenger.notification_onboarding_title')}</p>
          <p className="text-size-sm text-gray-60 mt-1">{t('messenger.notification_onboarding_body')}</p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isWorking}
          aria-label={t('messenger.notification_onboarding_dismiss')}
          data-testid={testingConstants.notificationOnboardingDismiss}
          className="text-gray-60 hover:text-black hover:bg-gray-10 shrink-0 flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors"
        >
          <X className="stroke-current h-4 w-4" />
        </button>
      </div>
      {isMuted && (
        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleEnableSound}
            disabled={isWorking}
            data-testid={testingConstants.notificationOnboardingEnableSound}
          >
            {t('messenger.notification_onboarding_enable_sound')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationOnboardingCard;
